import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { generateCardDetailsUrl, isValidCardId } from "@/lib/cards";

interface CardLinkProps {
  cardId: string;
  children: React.ReactNode;
  className?: string;
}

export const CardLink: React.FC<CardLinkProps> = ({
  cardId,
  children,
  className,
}) => {
  const navigate = useNavigate();
  const { shopId } = useParams();

  const handleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!shopId || !isValidCardId(cardId)) {
      console.error("Invalid shop ID or card ID for navigation", {
        shopId,
        cardId,
      });
      return;
    }

    const cardUrl = generateCardDetailsUrl(shopId, cardId);

    // Handle Ctrl/Cmd+click for opening in new tab
    if (event.ctrlKey || event.metaKey) {
      window.open(cardUrl, "_blank");
    } else {
      navigate(cardUrl);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();

      if (!shopId || !isValidCardId(cardId)) {
        console.error("Invalid shop ID or card ID for navigation", {
          shopId,
          cardId,
        });
        return;
      }

      navigate(generateCardDetailsUrl(shopId, cardId));
    }
  };

  return (
    <span
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="link"
      aria-label={`Navigate to card details for ${children}`}
      className={cn(
        "cursor-pointer text-primary underline decoration-primary/50 underline-offset-2",
        "hover:text-primary/80 hover:decoration-primary",
        "rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "transition-colors",
        className
      )}
    >
      {children}
    </span>
  );
};
